import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, HostBinding, Input, OnDestroy, TemplateRef } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbModal, NgbModalModule, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject, Subscription } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { ProductsService } from '../../_services/products.service';
import { DialogHeaderComponent } from '../dialog-header/dialog-header.component';

@Component({
  selector: 'app-header-product-search',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgbModalModule, DialogHeaderComponent],
  templateUrl: './header-product-search.component.html',
  styleUrls: ['./header-product-search.component.scss']
})
export class HeaderProductSearchComponent implements OnDestroy {
  private static nextId = 0;
  readonly inputId = `ia-product-search-${HeaderProductSearchComponent.nextId++}`;
  readonly titleId = `${this.inputId}-title`;
  readonly sku = new FormControl('', { nonNullable: true });
  @Input() compact = false;
  @HostBinding('class.ia-header-search--mobile') get mobile(): boolean { return this.compact; }
  @HostBinding('class.ia-header-search--desktop') get desktop(): boolean { return !this.compact; }
  searching = false;
  errorMessage = '';
  invalidSku = false;
  private dialogRef: NgbModalRef | undefined;
  private lookup: Subscription | undefined;
  private readonly destroy$ = new Subject<void>();

  constructor(private modal: NgbModal, private products: ProductsService, private router: Router) {}

  open(template: TemplateRef<unknown>): void {
    if (this.dialogRef) return;
    this.cancelLookup();
    this.sku.setValue('');
    this.clearError();
    const ref = this.modal.open(template, {
      ariaLabelledBy: this.titleId, centered: true, scrollable: true, size: 'sm',
      beforeDismiss: () => { this.cancelLookup(); return true; }
    });
    this.dialogRef = ref;
    ref.result.then(() => this.release(ref), () => this.release(ref));
  }

  dismiss(): void { this.dialogRef?.dismiss(); }

  clearError(): void { this.errorMessage = ''; this.invalidSku = false; }

  search(): void {
    const dialog = this.dialogRef;
    if (!dialog || this.searching) return;
    const sku = this.sku.value;
    if (!sku.trim()) {
      this.invalidSku = true;
      this.errorMessage = 'Enter a SKU.';
      return;
    }
    this.clearError();
    this.searching = true;
    this.lookup = this.products.getIdBySku(sku).pipe(
      take(1), takeUntil(this.destroy$), finalize(() => this.searching = false)
    ).subscribe({
      next: id => {
        if (this.dialogRef !== dialog) return;
        if (!Number.isInteger(id) || id <= 0) {
          this.invalidSku = true;
          this.errorMessage = 'No product found for this SKU.';
          return;
        }
        this.dialogRef = undefined;
        dialog.close();
        // Preserve the existing refresh sequence when opening another SKU from a product page.
        void this.router.navigate(['/'], { skipLocationChange: true }).then(() => this.router.navigate([`/products/${id}`]));
      },
      error: (error: unknown) => {
        if (this.dialogRef !== dialog) return;
        this.invalidSku = error instanceof HttpErrorResponse && error.status === 404;
        this.errorMessage = this.invalidSku ? 'No product found for this SKU.' : 'Could not search products. Try again.';
      }
    });
  }

  private cancelLookup(): void { this.lookup?.unsubscribe(); this.lookup = undefined; this.searching = false; }

  private release(ref: NgbModalRef): void {
    if (this.dialogRef !== ref) return;
    this.cancelLookup();
    this.dialogRef = undefined;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cancelLookup();
    this.dialogRef?.dismiss();
    this.dialogRef = undefined;
  }
}
